import { ReactNode } from "react";

import { DEFAULT_AUTO_HIDE_DURATION } from "./constants";
import {
  SnackbarInput,
  SnackbarItem,
  SnackbarOptions,
  SnackbarVariant,
} from "./types";

// Module level rather than context so non-React callers (services, interceptors)
// can raise a snackbar. Timers live in useSnackbarTimers, not here, because they
// pause on hover.

let items: SnackbarItem[] = [];
let counter = 0;

const listeners = new Set<() => void>();

const setItems = (next: SnackbarItem[]) => {
  items = next;
  listeners.forEach((listener) => listener());
};

export const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getSnapshot = () => items;

const show = (input: SnackbarInput): string => {
  counter += 1;

  const item: SnackbarItem = {
    ...input,
    id: `snackbar-${counter}`,
    variant: input.variant ?? "info",
    autoHideDuration:
      input.autoHideDuration === undefined
        ? DEFAULT_AUTO_HIDE_DURATION
        : input.autoHideDuration,
  };

  const replacedIndex = input.key
    ? items.findIndex((existing) => existing.key === input.key)
    : -1;

  if (replacedIndex === -1) {
    setItems([...items, item]);
    return item.id;
  }

  // Replace in place so a repeat does not jump the stack; the new id restarts
  // the timer.
  const replaced = items[replacedIndex];
  const next = [...items];
  next[replacedIndex] = item;
  setItems(next);
  replaced.onClose?.();

  return item.id;
};

const update = (id: string, patch: Partial<SnackbarInput>) => {
  if (!items.some((item) => item.id === id)) return;
  setItems(
    items.map((item) => (item.id === id ? { ...item, ...patch } : item))
  );
};

const dismiss = (id: string) => {
  const item = items.find((candidate) => candidate.id === id);
  if (!item) return;
  setItems(items.filter((candidate) => candidate.id !== id));
  item.onClose?.();
};

const dismissAll = () => {
  if (!items.length) return;
  const dismissed = items;
  setItems([]);
  dismissed.forEach((item) => item.onClose?.());
};

const withVariant =
  (variant: SnackbarVariant) =>
  (message: ReactNode, options: SnackbarOptions = {}) =>
    show({ ...options, message, variant });

export const snackbar = {
  show,
  update,
  dismiss,
  dismissAll,
  success: withVariant("success"),
  error: withVariant("error"),
  warning: withVariant("warning"),
  info: withVariant("info"),
};

export type SnackbarApi = typeof snackbar;
